import React from 'react'
import AnalyticsSparkline from '../charts/AnalyticsSparkline'
import { MetricCard } from '../../metric-card'
import { DashboardMetricValue } from '../../dashboard-metric-value'

export type AnalyticsKpiFormat = 'currency' | 'number' | 'percent'

export type AnalyticsKpiCardProps = {
  label: string
  value: number | null
  format?: AnalyticsKpiFormat
  labelHint?: string
  unavailable?: boolean
  unavailableLabel?: string
  deltaPct?: number | null
  previousValue?: number | null
  sparkline?: number[]
  accent?: 'primary' | 'success' | 'neutral'
  invertDelta?: boolean
  currencySymbol?: string
  className?: string
}

const toneByAccent = {
  primary: 'info',
  success: 'success',
  neutral: 'slate',
} as const

const AnalyticsKpiCard: React.FC<AnalyticsKpiCardProps> = ({
  label,
  value,
  format = 'number',
  labelHint,
  unavailable = false,
  unavailableLabel = '—',
  deltaPct,
  previousValue,
  sparkline,
  accent = 'neutral',
  invertDelta = false,
  currencySymbol,
  className,
}) => {
  const metricUnavailable = unavailable || value === null || value === undefined
  const isPercent = format === 'percent'
  const displayValue = isPercent && value !== null && value !== undefined ? `${Number(value).toFixed(1)}%` : value
  const comparison = previousValue !== undefined && previousValue !== null ? (
    <span dir="ltr">
      vs{' '}
      {isPercent ? (
        `${previousValue.toFixed(1)}%`
      ) : format === 'currency' ? (
        <span className="inline-flex text-[11px] font-medium tabular-nums">
          <DashboardMetricValue
            metricValue={previousValue}
            isCurrency
            currencySymbol={currencySymbol}
            kpiTier="secondary"
            compact
          />
        </span>
      ) : (
        previousValue.toLocaleString('en-US')
      )}
    </span>
  ) : null

  return (
    <MetricCard
      title={label}
      titleHint={labelHint}
      metricValue={format === 'currency' ? value : undefined}
      value={displayValue}
      isCurrency={format === 'currency'}
      currencySymbol={currencySymbol}
      tone={toneByAccent[accent]}
      variant={format === 'currency' ? 'financial' : 'operational'}
      unavailable={metricUnavailable}
      unavailableLabel={unavailableLabel}
      deltaPct={deltaPct}
      invertDelta={invertDelta}
      comparison={comparison}
      headerAdornment={
        sparkline && sparkline.length > 1 ? (
          <div className="w-14 shrink-0 opacity-80 sm:w-16">
            <AnalyticsSparkline data={sparkline} height={32} />
          </div>
        ) : null
      }
      className={className}
    />
  )
}

export default AnalyticsKpiCard
